import { forwardRef, useRef } from 'react';

import { groupBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { useReactToPrint } from 'react-to-print';

import { defaultCurrencyFormat } from 'src/common/defaultCurrencyFormat';
import { InvoiceRead } from 'src/common/external/bambi-api/bambiApi';
import { Button, ButtonProps } from 'src/common/primitives/Button';
import { Well } from 'src/common/primitives/Well/Well';

import { useAuth } from '../auth/useAuth';
import { InvoiceLineItemContainer } from './forms/InvoiceLineItemContainer';
import { PaymentsSection } from './management/PaymentsSection';
import { useInvoiceTotal } from './useInvoiceTotal';

const noop = function () {};

interface PrintableInvoiceProps {
  invoice: InvoiceRead;
}

export const PrintableInvoice = forwardRef<
  HTMLDivElement,
  PrintableInvoiceProps
>(({ invoice }, ref) => {
  const lineItems = invoice.line_items;
  const lineItemsByTrip = groupBy(lineItems, (lineItem) => lineItem.trip);

  const {
    currentOrganizationSettings,
    currentOrganizationName,
    currentOrganizationHeadQuarters,
  } = useAuth();

  const billingAddress =
    currentOrganizationSettings?.billing_address ||
    currentOrganizationHeadQuarters?.address;

  const { invoiceTotal, invoiceTotalDue } = useInvoiceTotal(invoice);

  return (
    <div className="flex flex-col gap-4 p-4 pt-8 screen:hidden" ref={ref}>
      <div className="mb-4 flex flex-row justify-between">
        <div>
          <h3 className="text-[24px] font-medium leading-6 text-gray-900">
            {currentOrganizationName}
          </h3>
          <div className="text-medium mb-4">{billingAddress}</div>
          <h3 className="text-[24px] font-medium leading-6 text-gray-900">
            Invoice Number: {invoice.number}
          </h3>
        </div>
        <div>{DateTime.now().toLocaleString()}</div>
      </div>
      {Object.values(lineItemsByTrip).map((group) => {
        return (
          <InvoiceLineItemContainer
            key={group[0].trip}
            lineItems={group}
            onAddLineItem={noop}
            onUpdateLineItem={noop}
            onDeleteLineItem={noop}
          />
        );
      })}
      <div className="mt-4 flex flex-row items-center justify-end gap-8">
        {lineItems.length ? (
          <>
            <div>Total:</div>
            <div className="text-xl">{defaultCurrencyFormat(invoiceTotal)}</div>
          </>
        ) : null}
      </div>

      <Well>
        <strong>Notes</strong>
        <p>{invoice.notes}</p>
      </Well>

      {invoice.payments?.length ? (
        <PaymentsSection
          payments={invoice.payments}
          invoiceTotalDue={invoiceTotalDue}
        />
      ) : null}
    </div>
  );
});

interface PrintableInvoiceTriggerProps extends ButtonProps {
  invoice: InvoiceRead;
}

export function PrintableInvoiceTrigger(props: PrintableInvoiceTriggerProps) {
  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Invoice Number ${props.invoice.number}`,
    // Bubble print from printing iframe to parent
    onBeforePrint: () => {
      window.onbeforeprint?.(new Event('onbeforeprint'));
    },
  });

  return (
    <>
      <PrintableInvoice invoice={props.invoice} ref={componentRef} />
      <Button {...props} onClick={handlePrint} />
    </>
  );
}
